import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Warning, Sparkle } from '@phosphor-icons/react';
import { fetchSession } from '@/lib/authJourney';
import { upgradeTrial } from '@/lib/billingApi';

function daysLeft(endsAt: string | null): number {
  if (!endsAt) return 0;
  const ms = new Date(endsAt).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / (24 * 60 * 60 * 1_000));
}

/**
 * Trial status strip shown at the top of the authenticated app shell.
 * - Active trial → "N days left in your free trial" + Upgrade.
 * - Expired trial → read-only notice + "Upgrade now" (the server blocks writes until they do).
 * Renders nothing for paid/non-trial workspaces. Trial state comes from the Session resolver
 * (workspaces[].trial), which is the single source of truth.
 */
export function TrialBanner() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['auth', 'session', 'trial'],
    queryFn: () => fetchSession('sign_in'),
    staleTime: 60 * 1_000,
  });

  const trial = useMemo(() => data?.workspaces.find((w) => w.trial)?.trial ?? null, [data]);

  const upgrade = useMutation({
    mutationFn: upgradeTrial,
    onSuccess: () => {
      toast.success('You\'re upgraded — welcome to the full version.');
      qc.invalidateQueries({ queryKey: ['auth', 'session', 'trial'] });
      // Reload so any read-only state left over from the expired trial is fully cleared.
      setTimeout(() => window.location.reload(), 600);
    },
    onError: () => toast.error('Upgrade failed. Please try again.'),
  });

  if (!trial) return null;

  const expired = !trial.active;
  const left = daysLeft(trial.endsAt);

  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${
        expired
          ? 'bg-red-600 text-white'
          : 'border-b border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200'
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        {expired ? (
          <Warning className="h-4 w-4 shrink-0" weight="fill" />
        ) : (
          <Sparkle className="h-4 w-4 shrink-0" weight="fill" />
        )}
        <span className="truncate">
          {expired
            ? 'Your free trial has ended — your workspace is read-only. Upgrade to start making changes again.'
            : `${left} day${left === 1 ? '' : 's'} left in your free trial.`}
        </span>
      </div>
      <button
        type="button"
        onClick={() => upgrade.mutate()}
        disabled={upgrade.isPending}
        className={`shrink-0 rounded px-3 py-1 text-xs font-semibold transition disabled:opacity-60 ${
          expired ? 'bg-white text-red-700 dark:text-red-300 hover:bg-red-50 dark:bg-red-500/10' : 'bg-amber-600 text-white hover:bg-amber-700'
        }`}
      >
        {upgrade.isPending ? 'Upgrading…' : expired ? 'Upgrade now' : 'Upgrade'}
      </button>
    </div>
  );
}
